/** The machine's state in words, for the card, the top bar's pill, the guided brew's header, setup and Home. */
import type { MachineConnection } from './store';
import type { MachineInfo, MachineState } from './types';

export interface MachineBadge {
  /** One or two words: "Ready", "Steam heating". */
  text: string;
  /** The status dot's class: '' (grey), 'ok', 'busy' (pulsing) or 'err'. */
  dot: '' | 'ok' | 'busy' | 'err';
  /** What to do about it, when there is something. */
  hint: string | null;
}

export const COOLING_HINT = 'Too hot to brew. Running water through the wand cools it faster.';

/** What the machine is doing, the way someone at it would say it. A brew or steam mode says more than the power. */
export function machineBadge(state: MachineState | null): MachineBadge {
  if (!state) return { text: 'Connected', dot: 'ok', hint: null };
  if (state.phase === 'fault') return { text: 'Fault', dot: 'err', hint: 'The machine reports a fault. Check it before the next shot.' };
  if (state.phase === 'brewing') return { text: 'Brewing', dot: 'busy', hint: null };
  if (state.steam === 'heating') return { text: 'Steam heating', dot: 'busy', hint: null };
  if (state.steam === 'ready') return { text: 'Steam ready', dot: 'ok', hint: null };
  switch (state.power) {
    case 'off':
      return { text: 'Off', dot: '', hint: null };
    case 'heating':
      return { text: 'Heating', dot: 'busy', hint: null };
    case 'ready':
      return { text: 'Ready', dot: 'ok', hint: null };
    case 'cooling':
      return { text: 'Cooling', dot: 'busy', hint: COOLING_HINT };
    case 'on':
      return { text: 'On', dot: 'ok', hint: null };
    default:
      return { text: 'Connected', dot: 'ok', hint: null };
  }
}

/** The badge for any connection state, not only a connected machine. */
export function connectionBadge(status: MachineConnection, badge: MachineBadge): MachineBadge {
  if (status === 'connected') return badge;
  if (status === 'error') return { text: 'Machine lost', dot: 'err', hint: null };
  return { text: status === 'connecting' ? 'Connecting…' : 'Reconnecting…', dot: 'busy', hint: null };
}

/** One line on the machine where a shot starts from (setup, Home): its state, what that means for the shot, and what to offer. */
export interface MachineLine {
  /** The badge's words: "Off", "Heating", "Machine lost". */
  text: string;
  dot: MachineBadge['dot'];
  /** A few words after it, when the state holds the shot up: "Too hot to brew yet." */
  note: string | null;
  /** Turn on for a machine that's off and can be turned on; Reconnect for one that was lost. */
  action: 'turnOn' | 'reconnect' | null;
}

/**
 * The machine before a shot: early enough that one that heats for a minute or more does it while the beans are
 * weighed and ground. null without a machine, so a setup without one shows nothing new.
 */
export function machineLine(status: MachineConnection, state: MachineState | null, info: MachineInfo | null): MachineLine | null {
  if (status === 'disconnected') return null;
  const badge = connectionBadge(status, machineBadge(state));
  const line = { text: badge.text, dot: badge.dot };
  if (status === 'error') return { ...line, note: null, action: 'reconnect' };
  if (status !== 'connected' || !state) return { ...line, note: null, action: null };
  if (state.phase === 'fault') return { ...line, note: 'Check it before the shot.', action: null };
  if (state.phase === 'brewing') return { ...line, note: null, action: null };
  if (state.steam === 'heating' || state.steam === 'ready') return { ...line, note: 'Switch it back to brew before the shot.', action: null };
  switch (state.power) {
    case 'off':
      return { ...line, note: null, action: info?.capabilities.power?.turnOn ? 'turnOn' : null };
    case 'heating':
      return { ...line, note: 'It heats while you weigh and grind.', action: null };
    case 'cooling':
      return { ...line, note: 'Too hot to brew yet.', action: null };
    default:
      return { ...line, note: null, action: null };
  }
}

/** "On for 4 min", from Status's on_elapsed_s; null when the device doesn't know since when (power.md). */
export function onForLabel(onSince: number | null, now: number): string | null {
  if (onSince == null) return null;
  const min = Math.floor(Math.max(0, now - onSince) / 60_000);
  if (min < 1) return 'On for under a minute';
  if (min < 60) return `On for ${min} min`;
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest > 0 ? `On for ${h} h ${rest} min` : `On for ${h} h`;
}

/** The device's own auto-off, which only follows a Turn on from an app (power.md, Auto-off). */
export function autoOffLabel(info: MachineInfo | null): string | null {
  const min = info?.capabilities.power?.autoOffMin;
  if (!min) return null;
  const span = min % 60 === 0 && min >= 120 ? `${min / 60} hours` : `${min} minutes`;
  return `Turned on from Taretto, it turns itself off after ${span} without a shot.`;
}
